import React, { useEffect, useMemo, useRef, useState } from "react";
import moment, { Moment } from "moment-timezone";
import { Alert, Box, Button, CircularProgress, Container, LinearProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/lab";
import AdapterMoment from "@mui/lab/AdapterMoment";
import { ISTableHeaderCell, ISTableRow, ISTableTitle } from "../../../Utils/ISTable";
import { formattedCurrency } from "../../../../utils/StringUtils";
import { getToken } from "../../../../utils/fetchApi";
import { connect } from 'react-redux'
import { AppState } from '../../../../reducers'
import { SessionState } from "../../../../reducers/auth/session";
import { useThemeContext } from "../../../../theme/ThemeContext";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";

const TIMEZONE =
  "America/Argentina/Buenos_Aires";

type ClientCalculation = {
  clientId: number;
  clientName: string;
  vendorId: number;
  zoneId: number;
  isChaco: boolean;

  monthlyTotals: Record<
    string,
    number
  >;

  positiveMonths: number;
  goal: number;

  count: number;
  totalClients: number;
};

type ProcessSummary = {
  runId: number;
  targetFrom: string;
  targetTo: string;
  sourceFrom: string;
  sourceTo: string;
  historicalMonths: number;
  clientsProcessed: number;
  goalsCreated: number;
  goalsDeactivated: number;
  productsWithoutPrice: number;
};

interface Props {
  session: SessionState
}

const GoalConstantAverageGeneration: React.FC<Props> = ({ session }) => {
  const { darkModeEnabled } = useThemeContext()
  const eventSourceRef =
    useRef<EventSource | null>(
      null
    );

  const [
    targetMonth,
    setTargetMonth
  ] = useState<Moment>(
    moment()
      .tz(TIMEZONE)      
      .startOf("month")
  );

  const [
    historicalMonths,
    setHistoricalMonths
  ] = useState<number>(3);

  const [
    isProcessing,
    setIsProcessing
  ] = useState(false);

  const [
    progress,
    setProgress
  ] = useState(0);

  const [
    progressMessage,
    setProgressMessage
  ] = useState("");

  const [
    clientProgress,
    setClientProgress
  ] = useState({
    count: 0,
    total: 0
  });

  const [
    rows,
    setRows
  ] = useState<
    ClientCalculation[]
  >([]);

  const [
    summary,
    setSummary
  ] = useState<
    ProcessSummary | null
  >(null);

  const [
    error,
    setError
  ] = useState<
    string | null
  >(null);

  const sourcePeriod =
    useMemo(() => {
      const sourceTo =
        targetMonth
          .clone()
          .subtract(1, "month")
          .endOf("month");

      const sourceFrom =
        targetMonth
          .clone()
          .subtract(
            historicalMonths,
            "months"
          )
          .startOf("month");

      return {
        from:
          sourceFrom.format(
            "DD/MM/YYYY"
          ),

        to:
          sourceTo.format(
            "DD/MM/YYYY"
          )
      };
    }, [
      targetMonth,
      historicalMonths
    ]);

  const historicalMonthKeys =
    useMemo(() => {
      return Array.from(
        {
          length: historicalMonths
        },
        (_, index) =>
          targetMonth
            .clone()
            .subtract(
              historicalMonths - index,
              "months"
            )
            .format("YYYY-MM")
      );
    }, [
      targetMonth,
      historicalMonths
    ]);

  const stopProcess = () => {
    eventSourceRef.current?.close();
    eventSourceRef.current =
      null;

    setIsProcessing(false);
  };

  const startProcess = () => {
    stopProcess();

    setRows([]);
    setSummary(null);
    setError(null);
    setProgress(0);
    setProgressMessage("");
    setClientProgress({
      count: 0,
      total: 0
    });
    setIsProcessing(true);

    const params =
      new URLSearchParams({ targetMonth: targetMonth.format("YYYY-MM"), historicalMonths: String(historicalMonths), access_token: getToken() ?? '' });

    const url = `${process.env.REACT_APP_API_URL}/goals/constant-average/generate?` + params.toString();

    const eventSource = new EventSource(url);

    eventSourceRef.current = eventSource;

    eventSource.addEventListener(
      "progress",
      event => {
        try {
          const parsed =
            JSON.parse(
              (
                event as MessageEvent
              ).data
            );
          setProgress(Number(parsed.progress ?? 0));
          setProgressMessage(String(parsed.message ?? "")
          );
        } catch (err) {
          console.error(
            "Error procesando progreso",
            err
          );
        }
      }
    );

    eventSource.addEventListener(
      "client",
      event => {
        try {
          const parsed =
            JSON.parse(
              (
                event as MessageEvent
              ).data
            ) as ClientCalculation;

          setClientProgress({
            count:
              parsed.count ?? 0,

            total:
              parsed.totalClients ?? 0
          });

          setProgress(
            parsed.totalClients
              ? 70 +
              (
                parsed.count /
                parsed.totalClients
              ) * 20
              : 70
          );

          setRows(previous => {
            const index =
              previous.findIndex(
                row =>
                  row.clientId ===
                  parsed.clientId
              );

            if (index < 0) {
              return [
                ...previous,
                parsed
              ];
            }

            const copy =
              previous.slice();

            copy[index] =
              parsed;

            return copy;
          });
        } catch (err) {
          console.error(
            "Error procesando cliente",
            err
          );
        }
      }
    );

    eventSource.addEventListener(
      "done",
      event => {
        try {
          const parsed =
            JSON.parse(
              (
                event as MessageEvent
              ).data
            ) as ProcessSummary;

          setSummary(parsed);
          setProgress(100);

          setProgressMessage(
            "Objetivos generados correctamente."
          );

          setIsProcessing(false);

          eventSource.close();

          eventSourceRef.current =
            null;
        } catch (err) {
          console.error(
            "Error procesando finalización",
            err
          );

          setError(
            "El proceso terminó, pero no se pudo interpretar la respuesta."
          );

          setIsProcessing(false);
        }
      }
    );

    eventSource.addEventListener(
      "failed",
      event => {
        try {
          const parsed =
            JSON.parse(
              (
                event as MessageEvent
              ).data
            );

          setError(
            parsed.error ??
            "Error generando objetivos."
          );
        } catch {
          setError(
            "Error generando objetivos."
          );
        }

        setIsProcessing(false);

        eventSource.close();

        eventSourceRef.current =
          null;
      }
    );

    eventSource.onerror = () => {
      if (
        eventSource.readyState ===
        EventSource.CLOSED
      ) {
        return;
      }

      setError(
        "Se perdió la conexión con el proceso."
      );

      setIsProcessing(false);

      eventSource.close();

      eventSourceRef.current =
        null;
    };
  };

  const downloadCalculatedGoals = () => {
    if (rows.length === 0) {
      return;
    }

    const sortedRows = [...rows].sort((a, b) => {
      if (a.zoneId !== b.zoneId) {
        return a.zoneId - b.zoneId;
      }

      if (a.vendorId !== b.vendorId) {
        return a.vendorId - b.vendorId;
      }

      return a.clientId - b.clientId;
    });

    const reportRows: Array<Array<string | number>> = [];

    /*
     * Encabezado general del reporte.
     */
    reportRows.push(["","","",
      "REPORTE DE OBJETIVOS POR PROMEDIO"
    ]);

    reportRows.push(["","","",
      "Mes objetivo",
      targetMonth.format("MM/YYYY")
    ]);

    reportRows.push(["","","",
      "Período histórico",
      `${sourcePeriod.from} - ${sourcePeriod.to}`
    ]);

    reportRows.push(["","","",
      "Meses históricos",
      historicalMonths
    ]);

    if (summary) {
      reportRows.push(["","","",
        "Ejecución",
        summary.runId
      ]);

      reportRows.push(["","","",
        "Clientes procesados",
        summary.clientsProcessed
      ]);

      reportRows.push(["","","",
        "Objetivos creados",
        summary.goalsCreated
      ]);

      reportRows.push(["","","",
        "Objetivos anteriores desactivados",
        summary.goalsDeactivated
      ]);

      reportRows.push(["","","",
        "Productos sin precio",
        summary.productsWithoutPrice
      ]);
    }

    /*
     * Fila vacía que separa el resumen de la tabla.
     */
    reportRows.push([]);

    /*
     * Guardamos dinámicamente la posición del encabezado.
     * Así no dependemos de si existe summary o no.
     */
    const headerRowIndex = reportRows.length;

    const tableHeaders = [
      "ZONA",
      "VENDEDOR",
      "CLIENTE",
      "NOMBRE",
      "CHACO",

      ...historicalMonthKeys.map(month =>
        moment(
          month,
          "YYYY-MM"
        ).format("MM/YYYY")
      ),

      "MESES CON VENTAS",
      "MESES HISTÓRICOS",
      "OBJETIVO"
    ];

    reportRows.push(tableHeaders);

    /*
     * Primera fila de datos, con índices base cero.
     */
    const firstDataRowIndex =
      reportRows.length;

    sortedRows.forEach(row => {
      reportRows.push([
        Number(row.zoneId ?? 0),
        Number(row.vendorId ?? 0),
        Number(row.clientId ?? 0),
        row.clientName ?? "",
        row.isChaco
          ? "SÍ"
          : "NO",

        ...historicalMonthKeys.map(month =>
          Number(
            row.monthlyTotals?.[month] ??
            0
          )
        ),

        Number(
          row.positiveMonths ??
          0
        ),

        Number(historicalMonths),

        Number(
          row.goal ??
          0
        )
      ]);
    });

    const worksheet =
      XLSX.utils.aoa_to_sheet(
        reportRows
      );

    const workbook =
      XLSX.utils.book_new();

    /*
     * Posiciones de columnas, con índice base cero.
     */
    const firstMonthColumnIndex = 5;

    const lastMonthColumnIndex =
      firstMonthColumnIndex +
      historicalMonthKeys.length -
      1;

    const positiveMonthsColumnIndex =
      firstMonthColumnIndex +
      historicalMonthKeys.length;

    const historicalMonthsColumnIndex =
      positiveMonthsColumnIndex + 1;

    const goalColumnIndex =
      historicalMonthsColumnIndex + 1;

    const lastColumnIndex =
      goalColumnIndex;

    const lastDataRowIndex =
      reportRows.length - 1;

    /*
     * Unimos la celda del título hasta la última columna.
     
    worksheet["!merges"] = [
      {
        s: {
          r: 0,
          c: 0
        },
        e: {
          r: 0,
          c: lastColumnIndex
        }
      }
    ];
    */
    /*
     * Anchos de columnas.
     */
    worksheet["!cols"] = [
      { wch: 12 }, // Zona
      { wch: 14 }, // Vendedor
      { wch: 14 }, // Cliente
      { wch: 42 }, // Nombre
      { wch: 10 }, // Chaco

      ...historicalMonthKeys.map(() => ({
        wch: 16
      })),

      { wch: 20 }, // Meses con ventas
      { wch: 20 }, // Meses históricos
      { wch: 18 }  // Objetivo
    ];

    /*
     * Aplicamos formato numérico a los identificadores
     * y cantidades enteras.
     */
    for (
      let rowIndex = firstDataRowIndex;
      rowIndex <= lastDataRowIndex;
      rowIndex++
    ) {
      const integerColumnIndexes = [
        0,
        1,
        2,
        positiveMonthsColumnIndex,
        historicalMonthsColumnIndex
      ];

      integerColumnIndexes.forEach(
        columnIndex => {
          const cellAddress =
            XLSX.utils.encode_cell({
              r: rowIndex,
              c: columnIndex
            });

          const cell =
            worksheet[cellAddress];

          if (!cell) {
            return;
          }

          /*
           * Se fuerza a número real.
           */
          cell.t = "n";
          cell.v = Number(cell.v ?? 0);
          cell.z = "0";
        }
      );

      /*
       * Importes mensuales.
       */
      for (
        let columnIndex =
          firstMonthColumnIndex;

        columnIndex <=
        lastMonthColumnIndex;

        columnIndex++
      ) {
        const cellAddress =
          XLSX.utils.encode_cell({
            r: rowIndex,
            c: columnIndex
          });

        const cell =
          worksheet[cellAddress];

        if (!cell) {
          continue;
        }

        cell.t = "n";
        cell.v = Number(cell.v ?? 0);

        /*
         * El valor sigue siendo numérico.
         * Excel mostrará los separadores según
         * la configuración regional del usuario.
         */
        cell.z = "#,##0.00";
      }

      /*
       * Objetivo calculado.
       */
      const goalCellAddress =
        XLSX.utils.encode_cell({
          r: rowIndex,
          c: goalColumnIndex
        });

      const goalCell =
        worksheet[goalCellAddress];

      if (goalCell) {
        goalCell.t = "n";
        goalCell.v =
          Number(goalCell.v ?? 0);

        goalCell.z = "#,##0.00";
      }
    }

    /*
     * Filtro sobre toda la tabla.
     */
    if (
      lastDataRowIndex >=
      headerRowIndex
    ) {
      worksheet["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: {
            r: headerRowIndex,
            c: 0
          },
          e: {
            r: lastDataRowIndex,
            c: lastColumnIndex
          }
        })
      };
    }

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Objetivos promedio"
    );

    const fileName =
      `generacion_objetivos_promedio_${targetMonth.format(
        "YYYY-MM"
      )}_industrias_saladillo.xlsx`;

    XLSX.writeFile(
      workbook,
      fileName
    );
  };

  useEffect(() => {
    return () => {
      eventSourceRef.current
        ?.close();
    };
  }, []);

  return (
    <Container
      maxWidth={false}
      sx={{
        height: "100%"
      }}
    >
      <Paper
        sx={{
          borderRadius: "10px",
          margin: "10px"
        }}
      >
        <ISTableTitle
          title={
            "Generación de objetivos por promedios a precios constantes"
          }
        />

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{
            padding: 2
          }}
        >
          <LocalizationProvider
            dateAdapter={
              AdapterMoment
            }
          >
            <DesktopDatePicker
              views={[
                "month",
                "year"
              ]}
              inputFormat="MM/YYYY"
              label="Mes objetivo"
              value={targetMonth}
              disabled={
                isProcessing
              }
              onChange={date => {
                if (date) {
                  setTargetMonth(
                    moment(date)
                      .tz(TIMEZONE)
                      .startOf(
                        "month"
                      )
                  );
                }
              }}
              renderInput={
                params => (
                  <TextField
                    {...params}
                    required
                    size="small"
                    sx={{
                      minWidth: 220
                    }}
                  />
                )
              }
            />
          </LocalizationProvider>

          <TextField
            select
            size="small"
            label={
              "Meses históricos"
            }
            value={
              historicalMonths
            }
            disabled={
              isProcessing
            }
            onChange={event =>
              setHistoricalMonths(
                Number(
                  event.target.value
                )
              )
            }
            sx={{
              minWidth: 220
            }}
          >
            {Array
              .from(
                {
                  length: 12
                },
                (_, index) =>
                  index + 1
              )
              .map(value => (
                <MenuItem
                  key={value}
                  value={value}
                >
                  {value}
                </MenuItem>
              ))}
          </TextField>

          <Button
            size="large"
            onClick={
              startProcess
            }
            disabled={
              isProcessing
            }
            style={{
              textTransform:
                "none"
            }}
          >
            Generar objetivos
          </Button>

          <Button
            size="large"
            onClick={
              stopProcess
            }
            disabled={
              !isProcessing
            }
            style={{
              textTransform:
                "none"
            }}
          >
            Cancelar
          </Button>
          <Button
            size="large"
            variant="outlined"
            startIcon={
              <FileDownload />
            }
            onClick={
              downloadCalculatedGoals
            }
            disabled={
              isProcessing ||
              rows.length === 0
            }
            style={{
              textTransform: "none"
            }}
          >
            Descargar reporte
          </Button>
        </Stack>

        <Box
          sx={{
            paddingLeft: 2,
            paddingRight: 2,
            paddingBottom: 2,
          }}
        >
          <Typography
            variant="body2"
          >
            Histórico a utilizar:{" "}
            <strong>
              {sourcePeriod.from}
            </strong>
            {" - "}
            <strong>
              {sourcePeriod.to}
            </strong>
            .
          </Typography>
        </Box>
      </Paper>

      <Paper
        sx={{
          borderRadius: "10px",
          margin: "10px",
          padding: 2
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
        >
          {isProcessing && (
            <CircularProgress
              size={22}
            />
          )}

          <Typography
            variant="body2"
            sx={{
              minWidth: 90
            }}
          >
            {clientProgress.count}/
            {clientProgress.total}
          </Typography>

          <Box
            sx={{
              flexGrow: 1
            }}
          >
            <LinearProgress
              color="success"
              variant="determinate"
              value={
                Math.min(
                  100,
                  progress
                )
              }
              sx={{
                height: 10,
                borderRadius: 5
              }}
            />
          </Box>
        </Stack>

        <Typography
          variant="body2"
          sx={{
            marginTop: 1
          }}
        >
          {progressMessage}
        </Typography>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{
            margin: "10px"
          }}
        >
          {error}
        </Alert>
      )}

      {summary && (
        <Alert
          severity="success"
          sx={{
            m: 1,

            backgroundColor:
              darkModeEnabled
                ? '#202020'
                : '#ffffff',

            color: '#008457',

            '& .MuiAlert-icon': {
              color: '#008457'
            },

            '& .MuiTypography-root': {
              color: 'inherit'
            }
          }}
        >
          <Typography>
            Ejecución:
            {" "}
            <strong>
              #{summary.runId}
            </strong>
          </Typography>

          <Typography>
            Clientes procesados:
            {" "}
            <strong>
              {summary.clientsProcessed}
            </strong>
          </Typography>

          <Typography>
            Objetivos creados:
            {" "}
            <strong>
              {summary.goalsCreated}
            </strong>
          </Typography>

          <Typography>
            Objetivos anteriores desactivados:
            {" "}
            <strong>
              {summary.goalsDeactivated}
            </strong>
          </Typography>

          <Typography>
            Productos sin precio:
            {" "}
            <strong>
              {summary.productsWithoutPrice}
            </strong>
          </Typography>
        </Alert>
      )}

      <Paper
        sx={{
          borderRadius: "10px",
          margin: "10px",
          overflow: "hidden"
        }}
      >
        <ISTableTitle
          title={
            "Objetivos calculados"
          }
        />

        <TableContainer
          sx={{
            maxHeight:
              "calc(100vh - 430px)"
          }}
        >
          <Table
            stickyHeader
            size="medium"
          >
            <TableHead>
              <TableRow
                selected
              >
                <ISTableHeaderCell
                  align="right"
                >
                  Cliente
                </ISTableHeaderCell>

                <ISTableHeaderCell>
                  Nombre
                </ISTableHeaderCell>

                <ISTableHeaderCell
                  align="right"
                >
                  Vendedor
                </ISTableHeaderCell>

                <ISTableHeaderCell
                  align="right"
                >
                  Zona
                </ISTableHeaderCell>

                <ISTableHeaderCell
                  align="center"
                >
                  Meses con ventas
                </ISTableHeaderCell>

                <ISTableHeaderCell
                  align="right"
                >
                  Objetivo
                </ISTableHeaderCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map(row => (
                <ISTableRow
                  key={
                    row.clientId
                  }
                  color="default"
                >
                  <TableCell
                    align="right"
                  >
                    {row.clientId}
                  </TableCell>

                  <TableCell>
                    {row.clientName}
                  </TableCell>

                  <TableCell
                    align="right"
                  >
                    {row.vendorId}
                  </TableCell>

                  <TableCell
                    align="right"
                  >
                    {row.zoneId}
                  </TableCell>

                  <TableCell
                    align="center"
                  >
                    {row.positiveMonths}
                    /
                    {historicalMonths}
                  </TableCell>

                  <TableCell
                    align="right"
                  >
                    {formattedCurrency(
                      row.goal
                    )}
                  </TableCell>
                </ISTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {isProcessing && (
          <Box
            display="flex"
            justifyContent="center"
            padding={2}
          >
            <CircularProgress
              size={25}
            />
          </Box>
        )}
      </Paper>
    </Container>
  );
};

const mapStateToProps = (state: AppState) => ({
  session: state.session
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(GoalConstantAverageGeneration)
